import { FC, lazy } from 'react';
import { TransactionsParams } from 'src/apis/capacity-management/types';
import { useCapacityCategories } from 'src/apis/capacity-management/use-capacity-categories';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMarketAreas } from 'src/apis/capacity-management/use-market-areas';
import { useMyTransactions } from 'src/apis/capacity-management/use-my-transactions';
import { Stack } from 'src/components/layout/stack';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { useGetSurrenderActions } from 'src/pages/reporting/my-transactions/use-get-surrender-actions';
import { usePageParams } from 'src/pages/reporting/my-transactions/use-page-params';
import { useSurrenderRoot } from 'src/pages/reporting/surrender/use-surrender-root';
import { useMemoOne } from 'use-memo-one';

const MyTransactionsPage = lazy(() => import('./page'));

export const ReportingTransactions: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const surrenderRoot = useSurrenderRoot();
  const capacityCategories = useCapacityCategories({ capacityManagementRoot });
  const marketAreas = useMarketAreas({ capacityManagementRoot });

  const pageParams = usePageParams();

  const params = useMemoOne<TransactionsParams>(() => {
    const {
      start,
      pageSize,
      networkPointId,
      tsoId,
      transactionTypeSpecificId,
      ...rest
    } = pageParams.value;
    return {
      offset: start,
      limit: pageSize,
      networkPointId: networkPointId.map((id) => id.value),
      tsoId: tsoId.map((id) => id.value),
      transactionTypeSpecificId: transactionTypeSpecificId
        ? [transactionTypeSpecificId]
        : [],
      ...rest,
    };
  }, [pageParams.value]);

  const myTransactions = useMyTransactions({
    capacityManagementRoot,
    params,
  });

  const surrenderActions = useGetSurrenderActions(
    myTransactions,
    surrenderRoot
  );

  if (!capacityManagementRoot.response) return <PageSpinner />;

  const hasMultiYearTransactions =
    capacityManagementRoot.response.data._links
      ?.multiYearTransactionTransportAllocation?.href;

  // version 2 of api adds Direct FCFS to the transactions table
  const isV2 = capacityManagementRoot.response.data._links?.myTransactionsV2;

  return (
    <Stack gap={2}>
      <Heading mode="title" data-testid="title-my-transactions">
        My Transactions
      </Heading>
      <MyTransactionsPage
        myTransactions={myTransactions}
        pageParams={pageParams}
        capacityCategories={capacityCategories}
        marketAreas={marketAreas}
        surrenderRoot={surrenderRoot}
        surrenderActions={surrenderActions}
        hasMultiYearTransactions={Boolean(hasMultiYearTransactions)}
        isV2={Boolean(isV2)}
      />
    </Stack>
  );
};
